import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api, { cachedGet } from '../api';
import { useEffect } from 'react';

const AssistantContext = createContext({
  activeAssistant: null,
  setAssistant: () => {},
  assistants: [],
});

export function useAssistant() {
  return useContext(AssistantContext);
}

export function useCurrentAssistantId() {
  const { publicId } = useParams();
  const [assistants, setAssistants] = useState([]);
  const [currentId, setCurrentId] = useState(() => {
    const stored = localStorage.getItem('active_assistant_id');
    return stored ? Number(stored) : null;
  });
  const [lastUsedAssistantId, setLastUsedAssistantId] = useState(null);
  const [idResolved, setIdResolved] = useState(false);
  const { activeAssistant, setAssistantsList } = useAssistant();

  // Resolve public_id → ID via API (no identifying info in URL)
  useEffect(() => {
    if (!publicId || publicId === 'default') {
      setIdResolved(true);
      return;
    }
    // Always resolve via API — public_id can be numeric too
    cachedGet(`/assistants/by-public/${publicId}`).then(res => {
      if (res.data?.id) {
        setCurrentId(res.data.id);
        localStorage.setItem('active_assistant_id', String(res.data.id));
      }
      setIdResolved(true);
    }).catch(() => setIdResolved(true));
  }, [publicId]);

  // Fetch assistants list once on mount
  useEffect(() => {
    cachedGet('/assistants').then(res => {
      const list = Array.isArray(res?.data?.assistants) ? res.data.assistants : [];
      const lastUsedId = res?.data?.last_used_assistant_id ?? null;
      setLastUsedAssistantId(lastUsedId);
      setAssistants(list);
      setAssistantsList(list);
      // Validate that currentId still exists in the list
      // If not, fall back to default or first assistant (any status)
      if (currentId) {
        const stillExists = list.some(a => a.id === currentId);
        if (!stillExists) {
          // 模式内解析链: 最近使用(仅 active) → 默认 → 列表首个
          const defaultAsst = list.find(a => a.id === lastUsedId && a.status === 'active')
            || list.find(a => a.is_default === 1);
          const firstAsst = list[0];
          const fallback = defaultAsst || firstAsst;
          if (fallback) {
            setCurrentId(fallback.id);
            localStorage.setItem('active_assistant_id', String(fallback.id));
          }
        }
      }
    }).catch(() => {});
  }, []);

  // Fallback: if no URL param, try activeAssistant from context
  useEffect(() => {
    if ((!publicId || publicId === 'default') && !currentId) {
      if (activeAssistant?.id) {
        setCurrentId(activeAssistant.id);
        localStorage.setItem('active_assistant_id', String(activeAssistant.id));
      } else {
        const stored = localStorage.getItem('active_assistant_id');
        if (stored && Number.isFinite(Number(stored))) {
          setCurrentId(Number(stored));
        }
      }
    }
  }, [publicId, assistants, activeAssistant, currentId]);

  return { currentId, assistants, lastUsedAssistantId, setCurrentId, idResolved };
}


export function AssistantProvider({ children }) {
  const [activeAssistant, setActiveAssistant] = useState(null);
  const [assistants, setAssistants] = useState([]);
  const assistantsRef = useRef([]);

  useEffect(() => {
    assistantsRef.current = assistants;
  }, [assistants]);

  const setAssistant = useCallback((idOrObj) => {
    let id = null;
    let nextAssistant = null;
    if (typeof idOrObj === 'object' && idOrObj !== null) {
      id = idOrObj.id;
      nextAssistant = idOrObj;
    } else if (idOrObj && assistants.length > 0) {
      const numId = Number(idOrObj);
      const found = assistantsRef.current.find(a => a.id === numId);
      id = numId;
      nextAssistant = found || { id: numId };
    } else {
      id = idOrObj ? Number(idOrObj) : null;
      nextAssistant = id != null ? { id } : null;
    }

    setActiveAssistant((prev) => {
      if (!nextAssistant) return prev === null ? prev : null;
      if (!prev || prev.id !== nextAssistant.id) return nextAssistant;
      const keys = new Set([...Object.keys(prev), ...Object.keys(nextAssistant)]);
      for (const key of keys) {
        if (prev[key] !== nextAssistant[key]) return { ...prev, ...nextAssistant };
      }
      return prev;
    });

    if (id != null) {
      localStorage.setItem('active_assistant_id', String(id));
    }
  }, []);

  const setAssistantsList = useCallback((updater) => {
    setAssistants((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!Array.isArray(next)) return prev;
      if (next === prev) return prev;
      const same = next.length === prev.length && next.every((item, index) => {
        const old = prev[index];
        return old
          && old.id === item.id
          && old.public_id === item.public_id
          && old.name === item.name
          && old.status === item.status
          && old.direction === item.direction
          && old.direction_name === item.direction_name
          && old.is_default === item.is_default;
      });
      return same ? prev : next;
    });
  }, []);

  const value = useMemo(() => ({
    activeAssistant,
    setAssistant,
    assistants,
    setAssistantsList,
  }), [activeAssistant, setAssistant, assistants, setAssistantsList]);

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
}
